import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAppData } from '../store';
import * as db from '../lib/db';
import { CATEGORY_EMOJI, RECURRENCE_LABELS } from '../types';
import type { Completion, Reminder } from '../types';
import { getUrgency, daysUntil } from '../lib/status';
import { isRecurring } from '../lib/recurrence';
import AttachmentsSection from './AttachmentsSection';

const SNOOZE_OPTIONS = [
  { label: 'Tomorrow', days: 1 },
  { label: '3 days', days: 3 },
  { label: '1 week', days: 7 },
];

export default function ObligationDetail() {
  const { obligationId } = useParams();
  const navigate = useNavigate();
  const { obligations, getAsset, refresh } = useAppData();
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [history, setHistory] = useState<Completion[]>([]);
  const [busy, setBusy] = useState(false);
  const [showSnooze, setShowSnooze] = useState(false);
  const [customSnoozeDate, setCustomSnoozeDate] = useState('');

  const obligation = obligations.find((o) => o.id === obligationId);

  useEffect(() => {
    if (!obligation) return;
    db.listReminders(obligation.id).then(setReminders);
    db.listCompletions(obligation.id).then(setHistory);
  }, [obligation?.id]);

  if (!obligation) return <div className="empty-state">Obligation not found.</div>;

  const asset = getAsset(obligation.asset_id);
  const urgency = getUrgency(obligation, reminders);
  const d = daysUntil(obligation.due_date);

  async function handleDone() {
    setBusy(true);
    try {
      await db.completeObligation(obligation!);
      await refresh();
      navigate(asset ? `/assets/${asset.id}` : '/');
    } finally {
      setBusy(false);
    }
  }

  async function handleSkip() {
    if (!confirm('Skip this occurrence? It will move to the next due date without being marked done.')) return;
    setBusy(true);
    try {
      await db.skipObligation(obligation!);
      await refresh();
      navigate(asset ? `/assets/${asset.id}` : '/');
    } finally {
      setBusy(false);
    }
  }

  async function handleSnooze(newDate: string) {
    if (!newDate) return;
    setBusy(true);
    try {
      await db.snoozeObligation(obligation!, newDate);
      await refresh();
      setShowSnooze(false);
      navigate(asset ? `/assets/${asset.id}` : '/');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!confirm(`Delete "${obligation!.name}"? This can't be undone.`)) return;
    setBusy(true);
    try {
      await db.deleteObligation(obligation!.id);
      await refresh();
      navigate(asset ? `/assets/${asset.id}` : '/');
    } finally {
      setBusy(false);
    }
  }

  function addDaysISO(days: number): string {
    const dt = new Date();
    dt.setDate(dt.getDate() + days);
    return dt.toISOString().slice(0, 10);
  }

  return (
    <div className="obligation-detail">
      <div className="page-header">
        <h1>
          {asset && <span className="emoji">{CATEGORY_EMOJI[asset.category]}</span>}
          {obligation.name}
        </h1>
        <span className={`badge urgency-${urgency}`}>
          {urgency === 'overdue'
            ? `${Math.abs(d)} day${Math.abs(d) === 1 ? '' : 's'} overdue`
            : d === 0
            ? 'Due today'
            : `Due in ${d} days`}
        </span>
      </div>

      {asset && (
        <Link to={`/assets/${asset.id}`} className="muted">
          {asset.name}
        </Link>
      )}

      <div className="detail-grid card">
        <Row label="Due date" value={obligation.due_date} />
        <Row label="Repeats" value={RECURRENCE_LABELS[obligation.recurrence_type]} />
        {reminders.length > 0 && (
          <Row
            label="Reminders"
            value={reminders
              .map((r) => (r.offset_days === 0 ? 'day of' : `${r.offset_days}d before`))
              .join(', ')}
          />
        )}
        {obligation.amount != null && <Row label="Amount" value={`$${obligation.amount}`} />}
        {obligation.payee && <Row label="Payee" value={obligation.payee} />}
        {obligation.account_reference && (
          <Row label="Account / ref" value={obligation.account_reference} />
        )}
        <Row label="Auto-pay" value={obligation.auto_pay ? 'Yes' : 'No'} />
        {obligation.description && <Row label="Description" value={obligation.description} />}
        {obligation.notes && <Row label="Notes" value={obligation.notes} />}
        {obligation.last_completed_date && (
          <Row label="Last completed" value={obligation.last_completed_date} />
        )}
      </div>

      {obligation.website && (
        <a
          href={obligation.website}
          target="_blank"
          rel="noreferrer"
          className="btn-primary pay-btn"
        >
          Pay / Open website
        </a>
      )}

      <div className="action-row">
        <button className="btn-primary" onClick={handleDone} disabled={busy}>
          ✓ Done
        </button>
        <button className="btn-secondary" onClick={() => setShowSnooze((s) => !s)} disabled={busy}>
          Snooze
        </button>
        {isRecurring(obligation.recurrence_type) && (
          <button className="btn-secondary" onClick={handleSkip} disabled={busy}>
            Skip
          </button>
        )}
        <Link to={`/obligations/${obligation.id}/edit`} className="btn-secondary">
          Edit
        </Link>
        <button className="btn-secondary danger" onClick={handleDelete} disabled={busy}>
          Delete
        </button>
      </div>

      {showSnooze && (
        <div className="snooze-panel card">
          {SNOOZE_OPTIONS.map((opt) => (
            <button
              key={opt.days}
              className="btn-secondary"
              onClick={() => handleSnooze(addDaysISO(opt.days))}
            >
              {opt.label}
            </button>
          ))}
          <div className="snooze-custom">
            <input
              type="date"
              value={customSnoozeDate}
              onChange={(e) => setCustomSnoozeDate(e.target.value)}
            />
            <button
              className="btn-secondary"
              onClick={() => handleSnooze(customSnoozeDate)}
              disabled={!customSnoozeDate}
            >
              Set
            </button>
          </div>
        </div>
      )}

      <AttachmentsSection parent={{ obligationId: obligation.id }} />

      {history.length > 0 && (
        <section className="history-section">
          <h2>History</h2>
          <ul className="history-list">
            {history.map((c) => (
              <li key={c.id}>
                {new Date(c.completed_date + 'T00:00:00').getFullYear()} —{' '}
                {c.status === 'skipped' ? 'Skipped' : 'Completed'} {c.completed_date}
                {c.amount != null ? ` · $${c.amount}` : ''}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="detail-row">
      <span className="muted">{label}</span>
      <span>{value}</span>
    </div>
  );
}
